import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { load as loadCashfree } from "@cashfreepayments/cashfree-js";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill, Button, EmptyState } from "../../components/common/Primitives";
import api, { formatApiError, API_BASE } from "../../lib/api";
import { Coins, Download, ShieldCheck, ShoppingCart } from "lucide-react";

function formatINR(v) {
  const n = Number(v || 0);
  return `₹${n.toLocaleString("en-IN")}`;
}

function StatusPill({ status }) {
  const tone = {
    pending_payment: "warn",
    paid: "gold",
    in_progress: "gold",
    delivered: "success",
    cancelled: "muted",
  }[status] || "muted";
  return <Pill tone={tone}>{(status || "").replace(/_/g, " ")}</Pill>;
}

export default function InvestorDiligenceOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/investor/daas/orders");
      setOrders(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const pay = async (order_id) => {
    setPaying(order_id);
    try {
      const { data } = await api.post(`/investor/daas/orders/${order_id}/pay`);
      if (data?.payment_session_id) {
        const cashfree = await loadCashfree({ mode: data.cashfree_mode || "sandbox" });
        cashfree.checkout({
          paymentSessionId: data.payment_session_id,
          redirectTarget: "_self",
        });
        return;
      }
      toast.info(data?.reason
        ? `Payment marked paid (Cashfree offline: ${String(data.reason).slice(0, 80)})`
        : "Payment recorded");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPaying(null);
    }
  };

  const downloadUrl = (order_id, doc_id) =>
    `${API_BASE}/daas/orders/${order_id}/deliverables/${doc_id}/download`;

  return (
    <DashboardShell title="Diligence Orders" subtitle="INVESTOR · DELIVERED REPORTS">
      <Toaster richColors position="top-right" />

      <div className="text-sm text-ink-soft mb-4 flex items-center gap-2">
        <Coins size={14} className="text-gold" />
        Every diligence order you&apos;ve placed on VenturaZ — status, deliverables and downloads in one place.
      </div>

      {loading ? (
        <div className="text-xs font-mono text-ink-muted" data-testid="investor-orders-loading">Loading…</div>
      ) : orders.length === 0 ? (
        <EmptyState
          testId="investor-orders-empty"
          title="No orders yet"
          message="Visit a Startup Passport and use the Diligence Store to place your first order."
          cta={
            <Link to="/investor/marketplace">
              <Button data-testid="investor-orders-empty-cta">
                <ShoppingCart size={14} className="mr-1" /> Browse Marketplace
              </Button>
            </Link>
          }
        />
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <Card key={o.order_id} testId={`investor-order-${o.order_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="overline flex items-center gap-1"><ShieldCheck size={12} /> ORDER · {o.order_id}</div>
                  <div className="font-heading text-ink text-lg mt-1">
                    {o.package_id ? `Package: ${o.package_id}` : `Modules: ${(o.module_ids || []).join(", ")}`}
                  </div>
                  <div className="text-xs text-ink-muted mt-1 font-mono">
                    On {o.startup_name || o.startup_id} · Created {(o.created_at || "").slice(0, 10)}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <StatusPill status={o.status} />
                  <div className="font-mono text-ink text-sm">{formatINR(o.total_inr)}</div>
                  {o.tier_discount_inr > 0 && (
                    <div className="text-[10px] text-ink-muted font-mono">
                      Tier {o.investor_tier} · −{formatINR(o.tier_discount_inr)} ({o.tier_discount_pct}%)
                    </div>
                  )}
                </div>
              </div>

              {/* Deliverables */}
              {(o.deliverables || []).length > 0 && (
                <div className="mt-4 border-t border-white/10 pt-3">
                  <div className="overline mb-2">DELIVERABLES ({o.deliverables.length})</div>
                  <div className="space-y-2">
                    {o.deliverables.map((d) => (
                      <div
                        key={d.doc_id}
                        className="flex items-center justify-between border border-white/10 rounded px-3 py-2"
                        data-testid={`investor-order-deliverable-${d.doc_id}`}
                      >
                        <div>
                          <div className="text-sm text-ink">{d.filename}</div>
                          <div className="text-[10px] font-mono text-ink-muted">
                            {(d.size / 1024).toFixed(1)} KB · {(d.uploaded_at || "").slice(0, 10)}
                          </div>
                        </div>
                        <a
                          href={downloadUrl(o.order_id, d.doc_id)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs font-mono uppercase tracking-wider text-gold hover:text-gold/80 transition flex items-center gap-1"
                          data-testid={`investor-order-download-${d.doc_id}`}
                        >
                          <Download size={12} /> Download
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="mt-4 flex items-center justify-end gap-2">
                {o.status === "pending_payment" && (
                  <Button
                    onClick={() => pay(o.order_id)}
                    disabled={paying === o.order_id}
                    data-testid={`investor-order-pay-${o.order_id}`}
                  >
                    {paying === o.order_id ? "Paying…" : "Pay (mock)"}
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}
